import { Fragment, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Trash2,
  Phone,
  AlertTriangle,
  Pencil,
  Save,
  X,
  Shield,
} from "lucide-react";
import { api } from "../../lib/api";
import {
  BrutalButton,
  BrutalCard,
  BrutalInput,
  BrutalLabel,
  BrutalTextarea,
  Badge,
  SectionTitle,
} from "../../components/common/Brutal";

const riskColor = (p, i) => {
  const score = p * i;
  if (score <= 5) return { bg: "#10B981", text: "#fff", label: "Low" };
  if (score <= 12) return { bg: "#F59E0B", text: "#3D1E6D", label: "Medium" };
  return { bg: "#EF4444", text: "#fff", label: "High" };
};

const PROB_LABELS = ["Rare", "Unlikely", "Possible", "Likely", "Almost Certain"];
const IMPACT_LABELS = ["Negligible", "Minor", "Moderate", "Major", "Catastrophic"];

const emptyRisk = {
  hazard: "",
  description: "",
  probability: 3,
  impact: 3,
  mitigation: "",
  owner: "",
};
const emptyIncident = {
  type: "",
  description: "",
  location: "",
  severity: "low",
  status: "open",
  reported_by: "Admin",
};
const emptyContact = { name: "", role: "", phone: "", department: "Medical" };

export default function AdminSafety() {
  const [tab, setTab] = useState("matrix");
  const [risks, setRisks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [riskForm, setRiskForm] = useState(emptyRisk);
  const [editingRisk, setEditingRisk] = useState(null);
  const [incForm, setIncForm] = useState(emptyIncident);
  const [editingInc, setEditingInc] = useState(null);
  const [contactForm, setContactForm] = useState(emptyContact);
  const [editingContact, setEditingContact] = useState(null);

  const load = async () => {
    const [r, i, c] = await Promise.all([
      api.get("/risks"),
      api.get("/incidents"),
      api.get("/emergency-contacts"),
    ]);
    setRisks(r.data);
    setIncidents(i.data);
    setContacts(c.data);
  };

  useEffect(() => { load(); }, []);

  const matrix = useMemo(() => {
    // 5x5 grid -> rows = impact (5..1 top->bottom), cols = probability (1..5 left->right)
    const grid = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
    for (const r of risks) {
      const p = Math.min(5, Math.max(1, r.probability));
      const i = Math.min(5, Math.max(1, r.impact));
      grid[5 - i][p - 1].push(r);
    }
    return grid;
  }, [risks]);

  // ---- Risk CRUD ----
  const submitRisk = async (e) => {
    e.preventDefault();
    if (!riskForm.hazard) return toast.error("Hazard required");
    try {
      if (editingRisk) {
        await api.put(`/risks/${editingRisk}`, {
          ...riskForm,
          probability: parseInt(riskForm.probability, 10),
          impact: parseInt(riskForm.impact, 10),
        });
      } else {
        await api.post("/risks", {
          ...riskForm,
          probability: parseInt(riskForm.probability, 10),
          impact: parseInt(riskForm.impact, 10),
        });
      }
      toast.success("Saved");
      setRiskForm(emptyRisk);
      setEditingRisk(null);
      load();
    } catch { toast.error("Failed"); }
  };
  const editRisk = (r) => {
    setEditingRisk(r.id);
    setRiskForm(r);
  };
  const removeRisk = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/risks/${id}`);
    load();
  };

  // ---- Incident CRUD ----
  const submitInc = async (e) => {
    e.preventDefault();
    if (!incForm.type) return toast.error("Type required");
    try {
      if (editingInc) await api.put(`/incidents/${editingInc}`, incForm);
      else await api.post("/incidents", incForm);
      toast.success("Saved");
      setIncForm(emptyIncident);
      setEditingInc(null);
      load();
    } catch { toast.error("Failed"); }
  };
  const editInc = (i) => { setEditingInc(i.id); setIncForm(i); };
  const removeInc = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/incidents/${id}`);
    load();
  };

  // ---- Contact CRUD ----
  const submitContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name) return toast.error("Name required");
    try {
      if (editingContact) await api.put(`/emergency-contacts/${editingContact}`, contactForm);
      else await api.post("/emergency-contacts", contactForm);
      toast.success("Saved");
      setContactForm(emptyContact);
      setEditingContact(null);
      load();
    } catch { toast.error("Failed"); }
  };
  const editContact = (c) => { setEditingContact(c.id); setContactForm(c); };
  const removeContact = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/emergency-contacts/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-safety">
      <SectionTitle kicker="Safety & Risk" title="Festival Safety Command" />

      <div className="flex flex-wrap gap-2">
        {[
          { v: "matrix", label: "Risk Matrix" },
          { v: "risks", label: "Risk Registry" },
          { v: "incidents", label: "Incident Logs" },
          { v: "contacts", label: "Emergency Contacts" },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`px-4 py-2 border-2 border-[#3D1E6D] font-bold text-xs uppercase tracking-widest ${
              tab === t.v
                ? "bg-[#E5247C] text-white shadow-[3px_3px_0_#3D1E6D]"
                : "bg-white"
            }`}
            data-testid={`safety-tab-${t.v}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "matrix" && (
        <BrutalCard className="p-5" data-testid="risk-matrix">
          <h3 className="display text-2xl">5×5 Risk Assessment Matrix</h3>
          <p className="text-xs text-[#6B4E9E]">
            Probability (likelihood) × Impact (severity). Each cell shows mapped hazards.
          </p>
          <div className="mt-4 overflow-x-auto">
            <div className="min-w-[700px]">
              <div className="grid grid-cols-[110px_repeat(5,1fr)] gap-1">
                <div />
                {PROB_LABELS.map((l, i) => (
                  <div
                    key={l}
                    className="text-[10px] font-bold uppercase tracking-widest text-center px-1 py-1"
                  >
                    {i + 1}. {l}
                  </div>
                ))}
                {[5, 4, 3, 2, 1].map((impact, ri) => (
                  <Fragment key={`row-${impact}`}>
                    <div
                      className="text-[10px] font-bold uppercase tracking-widest grid place-items-center px-1 text-right"
                    >
                      {impact}. {IMPACT_LABELS[impact - 1]}
                    </div>
                    {[1, 2, 3, 4, 5].map((prob) => {
                      const cellRisks = matrix[ri][prob - 1];
                      const c = riskColor(prob, impact);
                      return (
                        <div
                          key={`${impact}-${prob}`}
                          className="kf-border min-h-[80px] p-2 text-xs"
                          style={{ background: c.bg, color: c.text }}
                          data-testid={`risk-cell-${prob}-${impact}`}
                        >
                          <div className="font-bold text-[10px] uppercase tracking-widest opacity-80">
                            {c.label} ({prob * impact})
                          </div>
                          <div className="mt-1 space-y-0.5 leading-tight">
                            {cellRisks.map((r) => (
                              <div key={r.id} className="font-bold truncate">
                                · {r.hazard}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-3 text-xs">
            <Legend color="#10B981" label="Low (1-5)" />
            <Legend color="#F59E0B" label="Medium (6-12)" />
            <Legend color="#EF4444" label="High (13-25)" />
          </div>
        </BrutalCard>
      )}

      {tab === "risks" && (
        <>
          <BrutalCard className="p-5">
            <h3 className="display text-2xl mb-3">
              {editingRisk ? "Edit Risk" : "Add Risk"}
            </h3>
            <form onSubmit={submitRisk} className="grid sm:grid-cols-2 gap-3">
              <Field label="Hazard">
                <BrutalInput
                  value={riskForm.hazard}
                  onChange={(e) => setRiskForm({ ...riskForm, hazard: e.target.value })}
                  data-testid="risk-hazard"
                />
              </Field>
              <Field label="Owner">
                <BrutalInput
                  value={riskForm.owner}
                  onChange={(e) => setRiskForm({ ...riskForm, owner: e.target.value })}
                  data-testid="risk-owner"
                />
              </Field>
              <Field label="Probability (1-5)">
                <select
                  className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5"
                  value={riskForm.probability}
                  onChange={(e) => setRiskForm({ ...riskForm, probability: e.target.value })}
                  data-testid="risk-probability"
                >
                  {PROB_LABELS.map((l, i) => (
                    <option key={l} value={i + 1}>{i + 1} - {l}</option>
                  ))}
                </select>
              </Field>
              <Field label="Impact (1-5)">
                <select
                  className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5"
                  value={riskForm.impact}
                  onChange={(e) => setRiskForm({ ...riskForm, impact: e.target.value })}
                  data-testid="risk-impact"
                >
                  {IMPACT_LABELS.map((l, i) => (
                    <option key={l} value={i + 1}>{i + 1} - {l}</option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <BrutalLabel>Description</BrutalLabel>
                <BrutalTextarea
                  value={riskForm.description}
                  onChange={(e) => setRiskForm({ ...riskForm, description: e.target.value })}
                  data-testid="risk-description"
                />
              </div>
              <div className="sm:col-span-2">
                <BrutalLabel>Mitigation</BrutalLabel>
                <BrutalTextarea
                  value={riskForm.mitigation}
                  onChange={(e) => setRiskForm({ ...riskForm, mitigation: e.target.value })}
                  data-testid="risk-mitigation"
                />
              </div>
              <div className="sm:col-span-2 flex gap-2">
                <BrutalButton variant="primary" type="submit" data-testid="risk-save">
                  <Save className="h-4 w-4" /> {editingRisk ? "Update" : "Add"}
                </BrutalButton>
                {editingRisk && (
                  <BrutalButton
                    variant="outline"
                    onClick={() => { setEditingRisk(null); setRiskForm(emptyRisk); }}
                  >
                    <X className="h-4 w-4" /> Cancel
                  </BrutalButton>
                )}
              </div>
            </form>
          </BrutalCard>

          <div className="space-y-3">
            {risks.map((r) => {
              const c = riskColor(r.probability, r.impact);
              return (
                <BrutalCard key={r.id} className="p-4" data-testid={`risk-row-${r.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-[#E5247C]" />
                        <h4 className="display text-2xl">{r.hazard}</h4>
                      </div>
                      <p className="text-sm text-[#6B4E9E] mt-1">{r.description}</p>
                      <p className="text-sm mt-2"><b>Mitigation:</b> {r.mitigation}</p>
                      <p className="text-xs text-[#6B4E9E] mt-1">Owner: {r.owner}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Badge color={c.label === "Low" ? "green" : c.label === "Medium" ? "amber" : "red"}>
                        {c.label} · {r.probability}×{r.impact}
                      </Badge>
                      <div className="flex gap-1">
                        <BrutalButton
                          variant="outline"
                          className="px-2 py-1.5 text-xs"
                          onClick={() => editRisk(r)}
                          data-testid={`risk-edit-${r.id}`}
                        >
                          <Pencil className="h-3 w-3" />
                        </BrutalButton>
                        <BrutalButton
                          variant="dark"
                          className="px-2 py-1.5 text-xs"
                          onClick={() => removeRisk(r.id)}
                          data-testid={`risk-delete-${r.id}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </BrutalButton>
                      </div>
                    </div>
                  </div>
                </BrutalCard>
              );
            })}
          </div>
        </>
      )}

      {tab === "incidents" && (
        <>
          <BrutalCard className="p-5">
            <h3 className="display text-2xl mb-3">
              {editingInc ? "Edit Incident" : "Log Incident"}
            </h3>
            <form onSubmit={submitInc} className="grid sm:grid-cols-2 gap-3">
              <Field label="Type">
                <BrutalInput
                  value={incForm.type}
                  onChange={(e) => setIncForm({ ...incForm, type: e.target.value })}
                  data-testid="incident-type"
                />
              </Field>
              <Field label="Location">
                <BrutalInput
                  value={incForm.location}
                  onChange={(e) => setIncForm({ ...incForm, location: e.target.value })}
                  data-testid="incident-location"
                />
              </Field>
              <Field label="Severity">
                <select
                  className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5"
                  value={incForm.severity}
                  onChange={(e) => setIncForm({ ...incForm, severity: e.target.value })}
                  data-testid="incident-severity"
                >
                  <option>low</option>
                  <option>medium</option>
                  <option>high</option>
                </select>
              </Field>
              <Field label="Status">
                <select
                  className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5"
                  value={incForm.status}
                  onChange={(e) => setIncForm({ ...incForm, status: e.target.value })}
                  data-testid="incident-status"
                >
                  <option>open</option>
                  <option>in-progress</option>
                  <option>resolved</option>
                </select>
              </Field>
              <Field label="Reported By">
                <BrutalInput
                  value={incForm.reported_by}
                  onChange={(e) => setIncForm({ ...incForm, reported_by: e.target.value })}
                  data-testid="incident-reporter"
                />
              </Field>
              <div className="sm:col-span-2">
                <BrutalLabel>Description</BrutalLabel>
                <BrutalTextarea
                  value={incForm.description}
                  onChange={(e) => setIncForm({ ...incForm, description: e.target.value })}
                  data-testid="incident-description"
                />
              </div>
              <div className="sm:col-span-2 flex gap-2">
                <BrutalButton variant="primary" type="submit" data-testid="incident-save">
                  <Save className="h-4 w-4" /> {editingInc ? "Update" : "Log"}
                </BrutalButton>
                {editingInc && (
                  <BrutalButton
                    variant="outline"
                    onClick={() => { setEditingInc(null); setIncForm(emptyIncident); }}
                  >
                    <X className="h-4 w-4" /> Cancel
                  </BrutalButton>
                )}
              </div>
            </form>
          </BrutalCard>

          <div className="space-y-3">
            {incidents.map((i) => (
              <BrutalCard key={i.id} className="p-4" data-testid={`incident-row-${i.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <AlertTriangle className="h-5 w-5 text-[#E5247C]" />
                      <h4 className="display text-xl">{i.type}</h4>
                      <Badge
                        color={
                          i.severity === "high"
                            ? "red"
                            : i.severity === "medium"
                              ? "amber"
                              : "green"
                        }
                      >
                        {i.severity}
                      </Badge>
                      <Badge color={i.status === "resolved" ? "green" : i.status === "in-progress" ? "amber" : "red"}>
                        {i.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#6B4E9E] mt-1">{i.description}</p>
                    <p className="text-xs text-[#6B4E9E] mt-1">
                      {i.location} · by {i.reported_by} · {new Date(i.reported_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <BrutalButton
                      variant="outline"
                      className="px-2 py-1.5 text-xs"
                      onClick={() => editInc(i)}
                      data-testid={`incident-edit-${i.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </BrutalButton>
                    <BrutalButton
                      variant="dark"
                      className="px-2 py-1.5 text-xs"
                      onClick={() => removeInc(i.id)}
                      data-testid={`incident-delete-${i.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </BrutalButton>
                  </div>
                </div>
              </BrutalCard>
            ))}
            {incidents.length === 0 && (
              <BrutalCard className="p-6 text-center text-[#6B4E9E]">
                No incidents reported. Stay safe out there.
              </BrutalCard>
            )}
          </div>
        </>
      )}

      {tab === "contacts" && (
        <>
          <BrutalCard className="p-5">
            <h3 className="display text-2xl mb-3">
              {editingContact ? "Edit Contact" : "Add Emergency Contact"}
            </h3>
            <form onSubmit={submitContact} className="grid sm:grid-cols-2 gap-3">
              <Field label="Name">
                <BrutalInput
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  data-testid="contact-name"
                />
              </Field>
              <Field label="Role">
                <BrutalInput
                  value={contactForm.role}
                  onChange={(e) => setContactForm({ ...contactForm, role: e.target.value })}
                  data-testid="contact-role"
                />
              </Field>
              <Field label="Phone (+63 ...)">
                <BrutalInput
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  data-testid="contact-phone"
                />
              </Field>
              <Field label="Department">
                <select
                  className="w-full bg-white border-2 border-[#3D1E6D] px-3 py-2.5"
                  value={contactForm.department}
                  onChange={(e) => setContactForm({ ...contactForm, department: e.target.value })}
                  data-testid="contact-department"
                >
                  <option>Medical</option>
                  <option>Security</option>
                  <option>Safety</option>
                  <option>External</option>
                </select>
              </Field>
              <div className="sm:col-span-2 flex gap-2">
                <BrutalButton variant="primary" type="submit" data-testid="contact-save">
                  <Save className="h-4 w-4" /> {editingContact ? "Update" : "Add"}
                </BrutalButton>
                {editingContact && (
                  <BrutalButton
                    variant="outline"
                    onClick={() => { setEditingContact(null); setContactForm(emptyContact); }}
                  >
                    <X className="h-4 w-4" /> Cancel
                  </BrutalButton>
                )}
              </div>
            </form>
          </BrutalCard>

          <div className="grid sm:grid-cols-2 gap-3">
            {contacts.map((c) => (
              <BrutalCard key={c.id} className="p-4" data-testid={`contact-row-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <Badge color="orange">{c.department}</Badge>
                    <h4 className="display text-xl mt-2">{c.name}</h4>
                    <p className="text-sm text-[#6B4E9E]">{c.role}</p>
                    <a
                      href={`tel:${c.phone.replace(/\s/g, "")}`}
                      className="mt-2 inline-flex items-center gap-1 font-bold text-[#E5247C]"
                      data-testid={`contact-call-${c.id}`}
                    >
                      <Phone className="h-4 w-4" /> {c.phone}
                    </a>
                  </div>
                  <div className="flex flex-col gap-1">
                    <BrutalButton
                      variant="outline"
                      className="px-2 py-1.5 text-xs"
                      onClick={() => editContact(c)}
                      data-testid={`contact-edit-${c.id}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </BrutalButton>
                    <BrutalButton
                      variant="dark"
                      className="px-2 py-1.5 text-xs"
                      onClick={() => removeContact(c.id)}
                      data-testid={`contact-delete-${c.id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </BrutalButton>
                  </div>
                </div>
              </BrutalCard>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <BrutalLabel>{label}</BrutalLabel>
    {children}
  </div>
);

const Legend = ({ color, label }) => (
  <div className="flex items-center gap-1.5">
    <div className="h-4 w-6 kf-border" style={{ background: color }} />
    <span className="font-bold uppercase tracking-widest text-[10px]">{label}</span>
  </div>
);
